import { getObserver } from '@theme/coco-utilities';

/**
 * Product recommendations loader (IntersectionObserver + Section Rendering).
 * Custom element: <coco-recs>
 * Keep CSS classes (e.g. .product-recommendations) unchanged.
 */

const CE_NAME = 'coco-recs';

class CocoRecs extends HTMLElement {
  #observer = null;
  #loaded = false;

  get #region() {
    return (
      this.closest(
        '[data-recommendations-region], [data-cart-drawer-recommendations-region], [data-cart-recommendations-region]',
      ) || this.closest('[data-recommendations], .cart-recommendations')
    );
  }

  #hasProducts(root = this) {
    return !!root?.querySelector('[data-swiper-slide], coco-atc');
  }

  #setVisible(isVisible) {
    this.classList.toggle('hidden', !isVisible);
    this.#region?.classList.toggle('hidden', !isVisible);
  }

  connectedCallback() {
    if (this.#hasProducts()) {
      this.#setVisible(true);
      return;
    }

    if (this.#loaded || !this.dataset.url) return;

    if (window.Shopify?.designMode) {
      this.loadRecommendations();
      return;
    }

    const isInitiallyHidden =
      this.classList.contains('hidden') || this.#region?.classList.contains('hidden');

    if (!isInitiallyHidden && 'IntersectionObserver' in window) {
      this.#observer = getObserver({ rootMargin: '600px 0px', threshold: 0 }, true);
      this.#observer.observe(this, () => this.loadRecommendations());
    } else {
      this.loadRecommendations();
    }
  }

  disconnectedCallback() {
    this.#observer?.unobserve(this);
    this.#observer = null;
  }

  /** Public: used by cart drawer / cart list to force-load. */
  loadRecommendations() {
    if (this.#loaded || !this.dataset.url) return;
    this.#fetch();
  }

  /** Public: reset loaded flag then fetch (cart refresh path). */
  reloadRecommendations() {
    this.#loaded = false;
    this.loadRecommendations();
  }

  #fetch() {
    this.#loaded = true;

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('[id^="shopify-section-"]');

        if (!sectionInnerHTML) return;

        const recommendations = sectionInnerHTML.querySelector(CE_NAME);
        const hasProducts = this.#hasProducts(recommendations);

        if (hasProducts) {
          const sourceWrapper = recommendations?.querySelector('[data-swiper-wrapper]');
          const targetWrapper = this.querySelector('[data-swiper-wrapper]');

          if (sourceWrapper?.innerHTML.trim() && targetWrapper) {
            targetWrapper.innerHTML = sourceWrapper.innerHTML;
          } else {
            this.innerHTML = recommendations.innerHTML;
          }

          this.#setVisible(true);
          this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
        } else if (this.#region) {
          this.#setVisible(false);
          this.dispatchEvent(new CustomEvent('is-empty', { bubbles: true }));
        } else {
          this.#setVisible(false);
          this.closest('.shopify-section')?.remove();
          this.dispatchEvent(new CustomEvent('is-empty', { bubbles: true }));
        }
      })
      .catch((error) => {
        this.#loaded = false;
        console.error(error);
      });
  }
}

if (!customElements.get(CE_NAME)) {
  customElements.define(CE_NAME, CocoRecs);
}
